import { useState, useRef, useEffect } from "react";
import type { MarketplaceEntry } from "../types/index";
import { env, IS_DEV } from "../lib/env";

interface Props {
  plugin: MarketplaceEntry;
}

interface Method {
  label: string;
  command: string;
}

function CopyIcon(): JSX.Element {
  return (
    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <rect x="9" y="9" width="13" height="13" rx="2" ry="2" />
      <path d="M5 15H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v1" />
    </svg>
  );
}

function CheckIcon(): JSX.Element {
  return (
    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
      <polyline points="20 6 9 17 4 12" />
    </svg>
  );
}

function ChevronIcon({ expanded }: { expanded: boolean }): JSX.Element {
  return (
    <svg
      width="12"
      height="12"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2.5"
      strokeLinecap="round"
      strokeLinejoin="round"
      style={{ transform: expanded ? "rotate(180deg)" : "rotate(0deg)", transition: "transform 0.15s" }}
    >
      <polyline points="6 9 12 15 18 9" />
    </svg>
  );
}

function CopyRow({ label, command }: Method): JSX.Element {
  const [copied, setCopied] = useState(false);
  const [failed, setFailed] = useState(false);
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(() => () => { if (timerRef.current) clearTimeout(timerRef.current); }, []);

  const flash = (state: "copied" | "failed") => {
    setCopied(state === "copied");
    setFailed(state === "failed");
    if (timerRef.current) clearTimeout(timerRef.current);
    timerRef.current = setTimeout(() => { setCopied(false); setFailed(false); }, 2000);
  };

  const handleCopy = () => {
    if (!navigator.clipboard) { flash("failed"); return; }
    navigator.clipboard.writeText(command).then(() => flash("copied")).catch((err) => {
      flash("failed");
      if (IS_DEV) console.error("Clipboard write failed:", err);
    });
  };

  return (
    <div style={{ marginBottom: "1rem" }}>
      <p style={{ margin: "0 0 0.35rem 0", fontSize: "0.7rem", color: "var(--color-text-faint)", fontWeight: 600, textTransform: "uppercase", letterSpacing: "0.03em" }}>
        {label}
      </p>
      <div style={{ display: "flex", alignItems: "center", gap: "0.5rem", backgroundColor: "var(--color-surface-page)", borderRadius: "6px", padding: "0.5rem 0.75rem" }}>
        <code title={command} style={{ flex: 1, fontSize: "0.8rem", color: "var(--color-text-primary)", fontFamily: "monospace", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
          {command}
        </code>
        <button
          onClick={handleCopy}
          title={failed ? "Copy failed — select text manually" : "Copy command"}
          style={{
            flexShrink: 0,
            background: "none",
            border: "none",
            cursor: "pointer",
            padding: "2px",
            color: copied ? "var(--color-success)" : failed ? "var(--color-error)" : "var(--color-text-muted)",
            display: "flex",
            alignItems: "center",
            transition: "color 0.15s",
          }}
        >
          {copied ? <CheckIcon /> : <CopyIcon />}
        </button>
      </div>
    </div>
  );
}

export default function InstallInstructions({ plugin }: Props): JSX.Element {
  const [showHelp, setShowHelp] = useState(false);

  const installKey = plugin.installName
    ?? `${plugin.source}--${plugin.orgOrOwner.toLowerCase()}--${plugin.repoName.toLowerCase()}`;
  const marketplaceName = env.REACT_APP_MARKETPLACE_NAME;
  const catalogUrl = env.REACT_APP_CATALOG_GIT_URL;
  const methods: Method[] = [
    { label: "Claude Code", command: `claude plugin install ${installKey}@${marketplaceName}` },
  ];

  return (
    <div>
      {methods.map((m) => (
        <CopyRow key={m.label} {...m} />
      ))}

      <button
        type="button"
        aria-expanded={showHelp}
        onClick={() => setShowHelp(!showHelp)}
        style={{
          display: "flex",
          alignItems: "center",
          gap: "0.35rem",
          padding: 0,
          background: "none",
          border: "none",
          cursor: "pointer",
          fontSize: "0.8rem",
          color: "var(--color-brand)",
        }}
      >
        How to install via CLI
        <ChevronIcon expanded={showHelp} />
      </button>

      {showHelp && (
        <div style={{ marginTop: "0.5rem", fontSize: "0.8rem", lineHeight: "1.6", color: "var(--color-description)" }}>
          <p style={{ margin: "0 0 0.5rem 0", fontWeight: 600, color: "var(--color-text-primary)" }}>Claude Code</p>
          <ol style={{ margin: 0, paddingLeft: "1.25rem" }}>
            <li>First register the marketplace: <code>claude plugin marketplace add {catalogUrl}</code></li>
            <li>Then run the install command above from any terminal</li>
            <li>The plug-in is available when you next start Claude Code</li>
            <li>Or inside a session, run <code>/plugin install {installKey}@{marketplaceName}</code> then <code>/reload-plugins</code> to activate immediately</li>
          </ol>
        </div>
      )}
    </div>
  );
}
